import * as React from 'react';
import { Constants } from '@repo/shared';

interface User {
  access_token: string;
  username: string;
}

export interface AuthContextProps {
  isAuthenticated: boolean;
  login: (user: User) => void;
  logout: () => void;
  user: User | null;
}

const AuthContext = React.createContext<AuthContextProps | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = React.useState<User | null>(() => {
    const username = localStorage.getItem(Constants.USERNAME);
    const accessToken = localStorage.getItem(Constants.ACCESS_TOKEN);
    return username && accessToken
      ? { username, access_token: accessToken }
      : null;
  });

  const isAuthenticated = !!user;

  const login = React.useCallback(
    (_user: User) => {
      localStorage.setItem(Constants.USERNAME, _user.username);
      localStorage.setItem(Constants.ACCESS_TOKEN, _user.access_token);
      setUser(_user);
    },
    [setUser]
  );

  const logout = React.useCallback(() => {
    localStorage.removeItem(Constants.USERNAME);
    localStorage.removeItem(Constants.ACCESS_TOKEN);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
